import React from "react";
import { Alert, Box, MenuItem, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from "@mui/material";
import { useGetSyncLatestSuccessQuery, useGetSyncMetricsQuery } from "state/rtkQuery/sync/syncQueries";

import { formatDuration } from "./historyFilters";
import { formatDateTime, normalizeApiError, timeAgo } from "./syncFormat";
import { headCellSx, Panel, TaskName } from "./SyncParts";

export const LOOKBACKS = [
  { hours: 24, label: "Last 24 hours" },
  { hours: 168, label: "Last 7 days" },
  { hours: 720, label: "Last 30 days" },
];

export const percent = (value) => (value === null || value === undefined ? "—" : `${Number(value).toFixed(1).replace(/\.0$/, "")}%`);

// The status counts for a window. Absent status keys mean zero; a null rate means there was nothing finished to rate
// (the rate is success / (success + failure), so submitted, started, retry and revoked runs are left out of it).
export const readTotals = (metrics) => {
  const totals = metrics?.totals || {};
  const get = (key) => Number(totals[key] || 0);
  return { success: get("success"), failure: get("failure"), started: get("started"), submitted: get("submitted"), retry: get("retry"), revoked: get("revoked"), rate: metrics?.success_rate_percent ?? null };
};

// Per-task numbers and the last retained success for each task. Metrics select runs by when they were SUBMITTED.
const MetricsPanel = ({ lookbackHours, onLookbackChange, taskNames = "", refreshMs = 0 }) => {
  const { data: metrics, isFetching, isError, error } = useGetSyncMetricsQuery(
    { lookback_hours: lookbackHours, task_name: taskNames },
    { pollingInterval: refreshMs, skipPollingIfUnfocused: true }
  );
  const { data: latest } = useGetSyncLatestSuccessQuery(undefined, { pollingInterval: refreshMs, skipPollingIfUnfocused: true });
  const lastSuccess = Object.fromEntries((latest?.by_task_name || []).map((item) => [item.task_name, item.last_success_at]));
  const rows = metrics?.by_task_name || [];
  const failure = isError ? normalizeApiError(error) : null;

  return (
    <Panel
      title="By task"
      subtitle="runs submitted in the window"
      actions={
        <TextField select size="small" label="Window" value={lookbackHours} onChange={(event) => onLookbackChange(Number(event.target.value))} sx={{ width: 170 }}>
          {LOOKBACKS.map((option) => <MenuItem key={option.hours} value={option.hours}>{option.label}</MenuItem>)}
        </TextField>
      }
    >
      {isError ? (
        <Alert severity="info" sx={{ m: 2 }} data-testid="metrics-error">
          {failure.status === 403
            ? "You don't have permission to read run metrics."
            : failure.status === 404
              ? "Run metrics aren't available on this environment yet."
              : `Couldn't load the run metrics. ${failure.message}`}
        </Alert>
      ) : isFetching && !metrics ? (
        <Typography sx={{ p: 4, textAlign: "center", color: "#94a3b8" }}>Loading…</Typography>
      ) : rows.length === 0 ? (
        <Typography sx={{ p: 4, textAlign: "center", color: "#94a3b8" }}>No runs were submitted in this window.</Typography>
      ) : (
        <Table size="small" sx={{ opacity: isFetching ? 0.6 : 1 }}>
          <TableHead>
            <TableRow>
              {["Task", "Runs", "Succeeded", "Failed", "Running", "Avg duration (succeeded runs)", "Success rate", "Last success"].map((label) => <TableCell key={label} sx={headCellSx}>{label}</TableCell>)}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.task_name} data-testid="metrics-row">
                <TableCell><TaskName name={row.task_name} /></TableCell>
                <TableCell>{Number(row.total || 0)}</TableCell>
                <TableCell sx={{ color: "#4d7a10" }}>{Number(row.success || 0)}</TableCell>
                <TableCell sx={{ color: Number(row.failure) > 0 ? "#b91c1c" : undefined }}>{Number(row.failure || 0)}</TableCell>
                <TableCell>{Number(row.started || 0)}</TableCell>
                <TableCell>{row.avg_duration_seconds === null || row.avg_duration_seconds === undefined ? "—" : formatDuration(row.avg_duration_seconds)}</TableCell>
                <TableCell>{percent(row.success_rate_percent)}</TableCell>
                <TableCell sx={{ fontSize: 12.5 }} title={formatDateTime(lastSuccess[row.task_name])}>
                  {lastSuccess[row.task_name] ? timeAgo(lastSuccess[row.task_name]) : <span style={{ color: "#94a3b8" }}>No retained successful run</span>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <Box sx={{ p: "8px 20px 12px", color: "#64748b", fontSize: 12 }}>
        Success rate = succeeded ÷ (succeeded + failed); runs still going aren't counted, and the average duration covers succeeded runs only. A run counts when it was submitted, not when it finished. "Succeeded" means the worker finished: open a run to see whether it skipped the work.
      </Box>
    </Panel>
  );
};

export default MetricsPanel;
